import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useGarageWS } from "../hooks/useWebSocket";
import api, { backendUrl } from "../lib/api";
import { StatusPill } from "../components/StatusPill";
import { StatCard } from "../components/StatCard";
import { NotificationBell } from "../components/NotificationBell";
import { Timeline } from "../components/Timeline";
import { LogOut, IndianRupee, Send, RefreshCw, ReceiptText, ExternalLink, Search, Car, Clock, Wallet, Wrench, ShieldCheck, AlertTriangle, Eye, Ban } from "lucide-react";
import MacJitLogo from "../components/MacJitLogo";
import { toast } from "sonner";
import { isInternalWsEvent, showWsEventToast } from "../lib/wsNotifications";
import { isValidEmail, isValidIndianPhone, normalizeIndianPhone, isValidIndianPlate, formatIndianPlate } from "../lib/validation";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function ReceptionPage() {
  const { user, token, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [bays, setBays] = useState([]);
  const [stats, setStats] = useState({});
  const [services, setServices] = useState([]);
  const [tick, setTick] = useState(0);
  const [filter, setFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [search, setSearch] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);

  const load = async () => {
    const [b, c, m, ba, s, sv] = await Promise.all([
      api.get("/bookings", { params: requestFilters(filter, dateRange) }),
      api.get("/users/by-role/customer"),
      api.get("/users/by-role/mechanic"),
      api.get("/bays"),
      api.get("/admin/stats"),
      api.get("/services/active"),
    ]);
    setBookings(b.data); setCustomers(c.data); setMechanics(m.data); setBays(ba.data); setStats(s.data); setServices(sv.data);
  };

  useEffect(() => { load(); }, [filter, dateRange.from, dateRange.to]);
  useGarageWS(token, (e) => {
    if (isInternalWsEvent(e)) return;
    showWsEventToast(e, toast);
    load(); setTick((t) => t + 1);
  });

  const commandStats = buildCommandStats(bookings);
  const filteredBookings = filterBookings(bookings, search, filter);
  const selectedFresh = selectedBooking ? bookings.find((b) => b.id === selectedBooking.id) || selectedBooking : null;

  return (
    <div className="dark min-h-screen bg-zinc-950 text-zinc-100">
      <Header user={user} onLogout={logout} tick={tick} />
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="border border-zinc-800 bg-orange-500/5 px-4 py-2 flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-live-pulse" />
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Auto-allocation active · 1h 45m slots · 8:00 → 18:00</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800">
          <StatCard testid="stat-today-serviced" label="Today serviced" value={stats.today_serviced || 0} sub="paid jobs" accent />
          <StatCard testid="stat-today-revenue" label="Today revenue" value={`₹${stats.today_revenue || 0}`} />
          <StatCard testid="stat-active-bays" label="Active bays" value={stats.active_bays || 0} />
          <StatCard testid="stat-total-bookings" label="Total bookings" value={stats.total_bookings || 0} />
        </div>
        <CommandCards stats={commandStats} active={filter} onPick={setFilter} />

        <div className="grid lg:grid-cols-3 gap-6">
          <NewBookingCard customers={customers} bookings={bookings} services={services} onCreated={load} />
          <div className="lg:col-span-2 border border-zinc-800 bg-zinc-900/40 p-6">
            <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-3 mb-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Reception command center</p>
                <h2 className="font-display font-black text-2xl tracking-tight mt-1">Live Service Desk</h2>
                <p className="font-mono text-xs text-zinc-500 mt-1">Search by phone, plate, customer, mechanic, tester, or service.</p>
              </div>
              <QueueFilters filter={filter} setFilter={setFilter} dateRange={dateRange} setDateRange={setDateRange} onRefresh={load} />
            </div>
            <div className="mb-4 flex items-center gap-2 border border-zinc-800 bg-zinc-950 px-3 py-2 focus-within:border-orange-500">
              <Search className="w-4 h-4 text-orange-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search phone, plate, customer, mechanic..."
                className="w-full bg-transparent font-mono text-sm text-zinc-100 outline-none placeholder:text-zinc-600"
              />
            </div>
            <div className="space-y-3 max-h-[640px] overflow-y-auto">
              {filteredBookings.length === 0 && <p className="text-sm text-zinc-500">No matching bookings</p>}
              {filteredBookings.map((b) => (
                <BookingRow key={b.id} b={b} mechanics={mechanics} bays={bays} onUpdate={load} onOpen={() => setSelectedBooking(b)} />
              ))}
            </div>
          </div>
        </div>
      </main>
      <BookingDetailsDialog booking={selectedFresh} open={!!selectedBooking} onClose={() => setSelectedBooking(null)} onUpdate={load} />
    </div>
  );
}

const requestFilters = (filter, dateRange) => {
  const params = {};
  const clientOnlyFilters = ["ready_bill", "payment_due", "qa_failed"];
  if (filter && filter !== "all" && !clientOnlyFilters.includes(filter)) params.group = filter;
  if (dateRange.from) params.from = dateRange.from;
  if (dateRange.to) params.to = dateRange.to;
  return params;
};

const buildCommandStats = (bookings) => ({
  waiting: bookings.filter((b) => ["BOOKED", "ASSIGNED", "REOPENED"].includes(b.status)).length,
  ongoing: bookings.filter((b) => ["IN_SERVICE", "READY_TO_TEST", "READY_FOR_QA", "QA_FAILED"].includes(b.status)).length,
  readyBill: bookings.filter((b) => ["QA_DONE", "READY_FOR_BILL"].includes(b.status)).length,
  billed: bookings.filter((b) => b.status === "BILLED" && !b.paid).length,
  paid: bookings.filter((b) => b.status === "PAID" || b.paid).length,
  qaFailed: bookings.filter((b) => b.status === "QA_FAILED").length,
});

const filterBookings = (bookings, search, filter) => {
  let rows = bookings;
  if (filter === "ready_bill") rows = rows.filter((b) => ["QA_DONE", "READY_FOR_BILL"].includes(b.status));
  if (filter === "payment_due") rows = rows.filter((b) => b.status === "BILLED" && !b.paid);
  if (filter === "qa_failed") rows = rows.filter((b) => b.status === "QA_FAILED");
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((b) => [
    b.plate_number,
    b.customer_name,
    b.customer_phone,
    b.car_make,
    b.car_model,
    b.service_type,
    b.mechanic_name,
    b.tester_name,
    b.bay_name,
    b.status,
  ].some((value) => String(value || "").toLowerCase().includes(q)));
};

const CommandCards = ({ stats, active, onPick }) => {
  const cards = [
    { key: "waiting", label: "Waiting Desk", value: stats.waiting, icon: Clock, tone: "text-blue-400", hint: "Booked or assigned" },
    { key: "ongoing", label: "Ongoing Floor", value: stats.ongoing, icon: Wrench, tone: "text-orange-500", hint: "Service or QA" },
    { key: "ready_bill", label: "Ready Billing", value: stats.readyBill, icon: ReceiptText, tone: "text-emerald-400", hint: "QA completed" },
    { key: "payment_due", label: "Payment Due", value: stats.billed, icon: Wallet, tone: "text-yellow-400", hint: "Cash or Razorpay" },
    { key: "all", label: "Paid Today", value: stats.paid, icon: ShieldCheck, tone: "text-green-400", hint: "Completed" },
    { key: "qa_failed", label: "QA Rework", value: stats.qaFailed, icon: AlertTriangle, tone: "text-red-400", hint: "Back to mechanic" },
  ];
  return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-6 gap-3">
      {cards.map(({ key, label, value, icon: Icon, tone, hint }) => (
        <button
          key={`${label}-${key}`}
          onClick={() => onPick(key)}
          className={`group border bg-zinc-900/50 p-4 text-left transition-colors ${active === key ? "border-orange-500" : "border-zinc-800 hover:border-orange-500/70"}`}
        >
          <div className="flex items-center justify-between">
            <Icon className={`w-5 h-5 ${tone}`} />
            <span className="font-display text-2xl font-black">{value}</span>
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400 group-hover:text-zinc-200">{label}</p>
          <p className="mt-1 text-xs text-zinc-600">{hint}</p>
        </button>
      ))}
    </div>
  );
};

const QueueFilters = ({ filter, setFilter, dateRange, setDateRange, onRefresh }) => {
  const hasActiveFilter = filter !== "all" || dateRange.from || dateRange.to;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {filter !== "all" && (
        <span className="border border-orange-500/40 bg-orange-500/10 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-400">
          {filterLabel(filter)}
        </span>
      )}
      <input type="date" value={dateRange.from} onChange={(e) => setDateRange((d) => ({ ...d, from: e.target.value }))} className="bg-zinc-950 border border-zinc-800 px-3 py-2 font-mono text-xs text-zinc-300" />
      <input type="date" value={dateRange.to} onChange={(e) => setDateRange((d) => ({ ...d, to: e.target.value }))} className="bg-zinc-950 border border-zinc-800 px-3 py-2 font-mono text-xs text-zinc-300" />
      <button disabled={!hasActiveFilter} onClick={() => { setFilter("all"); setDateRange({ from: "", to: "" }); }} className="border border-zinc-800 text-zinc-400 hover:text-white disabled:opacity-40 disabled:hover:text-zinc-400 font-mono text-[10px] uppercase tracking-widest px-3 py-2">Clear</button>
      <button onClick={onRefresh} className="bg-zinc-800 hover:bg-zinc-700 text-white p-2"><RefreshCw className="w-4 h-4" /></button>
    </div>
  );
};

const filterLabel = (filter) => ({
  waiting: "Waiting desk",
  ongoing: "Ongoing floor",
  serviced: "Paid services",
  ready_bill: "Ready billing",
  payment_due: "Payment due",
  qa_failed: "QA rework",
})[filter] || filter;

const Header = ({ user, onLogout, tick }) => (
  <header className="sticky top-0 z-20 bg-zinc-950/90 backdrop-blur-xl border-b border-zinc-800">
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <MacJitLogo size={32} />
        <div>
          <p className="font-display font-black text-lg tracking-tighter">MACJIT <span className="text-orange-500">/ RECEPTION</span></p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{user?.name}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a href="/employee" data-testid="hr-link" className="border border-zinc-800 hover:border-orange-500 hover:text-orange-500 text-zinc-300 font-mono text-[10px] uppercase tracking-widest px-3 py-2 transition-colors">HR</a>
        <NotificationBell refreshKey={tick} />
        <button data-testid="logout-btn" onClick={onLogout} className="p-2 hover:bg-zinc-800 rounded-full"><LogOut className="w-4 h-4" /></button>
      </div>
    </div>
  </header>
);

const NewBookingCard = ({ customers, bookings = [], services = [], onCreated }) => {
  const emptyForm = { customer_name: "", customer_phone: "", customer_email: "", car_make: "", car_model: "", plate_number: "", service_type: "general", notes: "" };
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const visibleServices = services.filter((s) => s.key !== "ac-service");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (!isValidIndianPhone(form.customer_phone)) throw new Error("Enter a valid Indian mobile number");
      if (form.customer_email && !isValidEmail(form.customer_email)) throw new Error("Enter a valid customer email");
      if (!isValidIndianPlate(form.plate_number)) throw new Error("Enter a valid Indian car number, e.g. KA-05-MN-2024");
      await api.post("/bookings", {
        ...form,
        customer_phone: normalizeIndianPhone(form.customer_phone),
        customer_email: form.customer_email.trim().toLowerCase(),
        plate_number: formatIndianPlate(form.plate_number),
      });
      toast.success("Booking created · auto-assigned");
      onCreated();
      setForm(emptyForm);
    } catch (err) { toast.error(err.response?.data?.detail || err.message || "Failed"); }
    finally { setBusy(false); }
  };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const repeatMatches = bookings.filter((b) => {
    const email = form.customer_email.trim().toLowerCase();
    const phone = normalizeIndianPhone(form.customer_phone);
    return (email && String(b.customer_email || "").toLowerCase() === email)
      || (isValidIndianPhone(phone) && String(b.customer_phone || "") === phone);
  });
  const paidVisits = repeatMatches.filter((b) => b.paid || b.status === "PAID").length;
  const [autofilledKey, setAutofilledKey] = useState("");

  useEffect(() => {
    const match = repeatMatches[0];
    if (!match) {
      setAutofilledKey("");
      return;
    }
    const key = String(match.customer_email || match.customer_phone || match.customer_id || "");
    if (!key || key === autofilledKey) return;
    setForm((current) => ({
      ...current,
      customer_name: current.customer_name || match.customer_name || "",
      customer_phone: current.customer_phone || match.customer_phone || "",
      customer_email: current.customer_email || match.customer_email || "",
    }));
    setAutofilledKey(key);
  }, [repeatMatches, autofilledKey]);

  return (
    <div className="border border-zinc-800 bg-zinc-900/40 p-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-1">+ Walk-in</p>
      <h3 className="font-display font-black text-xl uppercase mb-4">New Booking</h3>
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="booking-cust-name" required placeholder="Customer name" value={form.customer_name} onChange={(e) => upd("customer_name", e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 font-mono text-sm focus:border-orange-500 outline-none" />
        <input data-testid="booking-cust-phone" required placeholder="+91 phone (auto-creates account)" value={form.customer_phone} onChange={(e) => upd("customer_phone", e.target.value)} onBlur={() => form.customer_phone && upd("customer_phone", normalizeIndianPhone(form.customer_phone))} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 font-mono text-sm focus:border-orange-500 outline-none" />
        <input data-testid="booking-cust-email" type="email" placeholder="Email for confirmation and OTP login" value={form.customer_email} onChange={(e) => upd("customer_email", e.target.value.trim().toLowerCase())} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 font-mono text-sm focus:border-orange-500 outline-none" />
        <input data-testid="booking-make" required placeholder="Car make (Hyundai)" value={form.car_make} onChange={(e) => upd("car_make", e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 font-mono text-sm focus:border-orange-500 outline-none" />
        <input data-testid="booking-model" required placeholder="Model (Creta)" value={form.car_model} onChange={(e) => upd("car_model", e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 font-mono text-sm focus:border-orange-500 outline-none" />
        <input data-testid="booking-plate" required placeholder="Plate (KA-05-MN-2024)" value={form.plate_number} onChange={(e) => upd("plate_number", e.target.value.toUpperCase())} onBlur={() => form.plate_number && isValidIndianPlate(form.plate_number) && upd("plate_number", formatIndianPlate(form.plate_number))} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 font-mono text-sm focus:border-orange-500 outline-none" />
        {repeatMatches.length > 0 && (
          <div className="border border-emerald-500/40 bg-emerald-500/10 p-3 font-mono text-xs text-emerald-200">
            Repeat customer detected · {repeatMatches.length} visit{repeatMatches.length === 1 ? "" : "s"} found · {paidVisits} paid service{paidVisits === 1 ? "" : "s"}
            <div className="mt-2 text-emerald-100/80">
              Customer details filled from previous visit. Car details can be changed for a different vehicle.
            </div>
          </div>
        )}
        <Select value={form.service_type} onValueChange={(v) => upd("service_type", v)}>
          <SelectTrigger data-testid="booking-service" className="bg-zinc-950 border-zinc-800"><SelectValue placeholder="Select service" /></SelectTrigger>
          <SelectContent>
            {visibleServices.length > 0 ? visibleServices.map((s) => {
              const h = s.duration_min >= 60 ? `${Math.floor(s.duration_min/60)}h${s.duration_min%60 > 0 ? " " + s.duration_min%60 + "m" : ""}` : `${s.duration_min}m`;
              return <SelectItem key={s.key} value={s.key}>{s.name} · {h} · ₹{s.base_price}</SelectItem>;
            }) : (
              <>
                <SelectItem value="general">General Service · 2h</SelectItem>
                <SelectItem value="oil-change">Oil & Filter Change · 45m</SelectItem>
                <SelectItem value="full-service">Full Service · 3h 30m</SelectItem>
                <SelectItem value="alignment">Wheel Alignment & Balancing · 1h</SelectItem>
                <SelectItem value="brake">Brake Service · 1h 15m</SelectItem>
                <SelectItem value="engine">Engine Repair / Diagnostics · 4h</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
        <textarea placeholder="Notes" value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 font-mono text-sm" rows={2} />
        <button data-testid="booking-submit" type="submit" disabled={busy} className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-black font-display font-black uppercase tracking-widest py-3 transition-colors">
          {busy ? "Creating..." : "Create + Auto-Assign"}
        </button>
      </form>
    </div>
  );
};

const ActionButtons = ({ b, onUpdate, compact = false }) => {
  const [localPaymentLink, setLocalPaymentLink] = useState(b.payment_link || b.internal_payment_link || "");
  const [billOpen, setBillOpen] = useState(false);
  const [billPreview, setBillPreview] = useState(null);
  const [discount, setDiscount] = useState(String(b.discount || 0));
  const [manualLabel, setManualLabel] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const reassign = async () => { await api.post(`/bookings/${b.id}/auto-assign`); toast.success("Re-assigned"); onUpdate(); };
  const cancelService = async () => {
    setCancelBusy(true);
    try {
      await api.post(`/bookings/${b.id}/cancel`, { reason: cancelReason });
      toast.success("Service cancelled");
      setCancelOpen(false);
      setCancelReason("");
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not cancel service"); }
    finally { setCancelBusy(false); }
  };
  const openBill = async () => {
    const manual_lines = manualLabel && Number(manualAmount || 0) ? [{ label: manualLabel, amount: Number(manualAmount) }] : [];
    const r = await api.post(`/bookings/${b.id}/bill-preview`, { extra_discount: Number(discount || 0), manual_lines });
    setBillPreview(r.data);
    setBillOpen(true);
  };
  const generateBill = async () => {
    const manual_lines = manualLabel && Number(manualAmount || 0) ? [{ label: manualLabel, amount: Number(manualAmount) }] : [];
    await api.post(`/bookings/${b.id}/bill`, { extra_discount: Number(discount || 0), manual_lines });
    toast.success("Bill generated");
    setBillOpen(false);
    onUpdate();
  };
  const collectPayment = async () => { await api.post(`/bookings/${b.id}/pay`); toast.success("Payment collected"); onUpdate(); };
  const payLink = localPaymentLink || b.payment_link || b.internal_payment_link || "";
  const resolvedPayLink = payLink?.startsWith("/") ? `${window.location.origin}${payLink}` : payLink;
  const sendPaymentLink = async () => {
    const r = await api.post(`/bookings/${b.id}/payment-link`);
    const link = r.data.payment_link || r.data.internal_payment_link || "";
    setLocalPaymentLink(link);
    const notification = r.data.payment_link_notification || {};
    toast.success("Payment link ready", {
      description: notification?.sent ? "Sent to customer email" : (notification?.reason || "Link generated for desk sharing"),
    });
    onUpdate();
  };
  const canGenerateBill = ["QA_DONE", "READY_FOR_BILL"].includes(b.status);
  const canCancel = !["CANCELLED", "BILLED", "PAID"].includes(b.status) && !b.paid;
  const base = compact ? "px-3 py-2" : "px-4 py-2";

  return (
    <div className="flex flex-wrap gap-2">
      {["BOOKED", "ASSIGNED"].includes(b.status) && (
        <button data-testid={`reassign-btn-${b.id}`} onClick={reassign} className={`border border-zinc-700 hover:border-orange-500 hover:text-orange-500 text-zinc-300 font-mono text-xs uppercase tracking-widest ${base} transition-colors`}>↻ Re-assign</button>
      )}
      {canGenerateBill && (
        <button data-testid={`bill-btn-${b.id}`} onClick={openBill} className={`bg-orange-500 hover:bg-orange-400 text-black font-mono text-xs uppercase tracking-widest font-bold ${base} transition-colors`}>Review Bill</button>
      )}
      {b.status === "BILLED" && !b.paid && (
        <button onClick={openBill} className={`border border-orange-500/50 text-orange-500 hover:bg-orange-500 hover:text-black font-mono text-xs uppercase tracking-widest font-bold ${base} transition-colors`}>Modify Bill</button>
      )}
      {b.status === "BILLED" && !b.paid && (
        <button data-testid={`collect-btn-${b.id}`} onClick={collectPayment} className={`bg-emerald-500 hover:bg-emerald-400 text-black font-mono text-xs uppercase tracking-widest font-bold ${base} transition-colors flex items-center gap-1`}><IndianRupee className="w-3 h-3" />Collect ₹{b.bill_amount}</button>
      )}
      {b.status === "BILLED" && !b.paid && (
        <button data-testid={`payment-link-btn-${b.id}`} onClick={sendPaymentLink} className={`bg-blue-500 hover:bg-blue-400 text-black font-mono text-xs uppercase tracking-widest font-bold ${base} transition-colors flex items-center gap-1`}><Send className="w-3 h-3" />Send Razor Link</button>
      )}
      {b.status === "BILLED" && resolvedPayLink && (
        <a href={resolvedPayLink} target="_blank" rel="noreferrer" className={`border border-zinc-700 hover:border-orange-500 text-zinc-300 font-mono text-xs uppercase tracking-widest ${base} transition-colors`}>View Pay Link</a>
      )}
      {["BILLED", "PAID"].includes(b.status) && (
        <a
          href={backendUrl(`/api/invoices/${b.id}.pdf`)}
          target="_blank"
          rel="noreferrer"
          className={`border border-zinc-700 hover:border-orange-500 text-zinc-300 font-mono text-xs uppercase tracking-widest ${base} transition-colors flex items-center gap-1`}
        >
          <ReceiptText className="w-3 h-3" /> Open Invoice <ExternalLink className="w-3 h-3" />
        </a>
      )}
      {canCancel && (
        <button
          data-testid={`cancel-service-btn-${b.id}`}
          onClick={() => setCancelOpen(true)}
          className={`border border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white font-mono text-xs uppercase tracking-widest ${base} transition-colors flex items-center gap-1`}
        >
          <Ban className="w-3 h-3" /> Cancel
        </button>
      )}
      <Dialog open={billOpen} onOpenChange={setBillOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
          <DialogHeader><DialogTitle className="font-display uppercase">Billing review</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Discount ₹</label>
                <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm" />
              </div>
              <button onClick={openBill} className="self-end border border-zinc-700 text-zinc-300 px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Refresh</button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input value={manualLabel} onChange={(e) => setManualLabel(e.target.value)} placeholder="Add-on label" className="bg-black border border-zinc-800 px-3 py-2 font-mono text-sm" />
              <input type="number" value={manualAmount} onChange={(e) => setManualAmount(e.target.value)} placeholder="Amount ₹" className="bg-black border border-zinc-800 px-3 py-2 font-mono text-sm" />
            </div>
            <div className="bg-black border border-zinc-800 p-3 space-y-1 text-sm">
              {(billPreview?.base_lines || []).map((l) => <div key={l.key} className="flex justify-between"><span>{l.label}</span><span>₹{l.amount}</span></div>)}
              <div className="flex justify-between text-zinc-400"><span>Discount</span><span>-₹{billPreview?.discount || 0}</span></div>
              {(billPreview?.billing_addons || []).map((l, i) => <div key={`${l.key}-${i}`} className="flex justify-between text-orange-400"><span>{l.label}</span><span>₹{l.amount}</span></div>)}
              <div className="flex justify-between border-t border-zinc-800 pt-2 font-display font-black text-orange-500"><span>Total</span><span>₹{billPreview?.total || b.bill_amount || 0}</span></div>
            </div>
            <button onClick={generateBill} className="w-full bg-orange-500 hover:bg-orange-400 text-black font-display font-black uppercase tracking-widest py-3">Finalize Bill</button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">Cancel service</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="border border-red-500/30 bg-red-500/10 p-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-red-300">This will unassign mechanic, tester, and bay</p>
              <p className="mt-1 text-sm text-red-100">{b.plate_number} · {b.customer_name}</p>
            </div>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason shared by customer"
              rows={3}
              className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm outline-none focus:border-red-500"
            />
            <button
              disabled={cancelBusy}
              onClick={cancelService}
              className="w-full bg-red-500 hover:bg-red-400 disabled:opacity-50 text-white font-display font-black uppercase tracking-widest py-3"
            >
              {cancelBusy ? "Cancelling..." : "Confirm cancellation"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const BookingRow = ({ b, mechanics, bays, onUpdate, onOpen }) => {

  const eta = b.estimated_start_at ? new Date(b.estimated_start_at) : null;
  const etaEnd = b.estimated_end_at ? new Date(b.estimated_end_at) : null;
  const fmtTime = (d) => d?.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  const age = b.created_at ? timeSince(new Date(b.created_at)) : "";

  return (
    <div data-testid={`booking-row-${b.id}`} className="border border-zinc-800 bg-zinc-950/60 p-4 hover:border-orange-500/50 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">{b.id.slice(0, 8)}</p>
          <p className="font-display font-bold text-lg flex items-center gap-2"><Car className="w-4 h-4 text-orange-500" />{b.plate_number} <span className="text-zinc-500 text-sm font-normal">{b.car_make} {b.car_model}</span></p>
          <p className="font-mono text-xs text-zinc-400">
            {b.customer_name} · {b.service_type}
            {b.bay_name ? ` · ${b.bay_name}` : ""}
            {b.mechanic_name ? ` · Mechanic: ${b.mechanic_name}` : ""}
            {b.tester_name ? ` · Tester: ${b.tester_name}` : ""}
          </p>
          {b.tester_name && (
            <p className="font-mono text-[10px] text-blue-400 mt-1">
              QA assigned to {b.tester_name}{b.tester_phone ? ` · ${b.tester_phone}` : ""}
            </p>
          )}
          {eta && (
            <p className="font-mono text-[10px] text-orange-500 mt-1">
              ETA {fmtTime(eta)}{etaEnd ? ` → ${fmtTime(etaEnd)}` : ""}
              {b.auto_assigned && <span className="ml-2 px-1.5 py-0.5 bg-orange-500/10 border border-orange-500/30 uppercase tracking-widest">Auto</span>}
            </p>
          )}
          {age && <p className="font-mono text-[10px] text-zinc-600 mt-1">Booked {age}</p>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusPill status={b.status} />
          <button onClick={onOpen} className="border border-zinc-700 hover:border-orange-500 hover:text-orange-500 text-zinc-300 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 flex items-center gap-1"><Eye className="w-3 h-3" />Details</button>
        </div>
      </div>
      <div className="mt-3"><ActionButtons b={b} onUpdate={onUpdate} /></div>
    </div>
  );
};

const BookingDetailsDialog = ({ booking, open, onClose, onUpdate }) => {
  if (!booking) return null;
  const qaFailed = booking.status === "QA_FAILED";
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="top-[52%] max-h-[86vh] max-w-4xl overflow-hidden border-zinc-800 bg-zinc-950 p-0 text-zinc-100">
        <DialogHeader className="sticky top-0 z-10 border-b border-zinc-800 bg-zinc-950/95 px-6 py-4 backdrop-blur">
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {booking.plate_number} · {booking.customer_name}
          </DialogTitle>
        </DialogHeader>
        <div className="grid max-h-[calc(86vh-82px)] overflow-y-auto p-6 lg:grid-cols-[1fr_280px] gap-6">
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Info label="Customer" value={`${booking.customer_name || "-"} · ${booking.customer_phone || "-"}`} />
              <Info label="Car" value={`${booking.car_make || "-"} ${booking.car_model || ""}`} />
              <Info label="Service" value={booking.service_type || "-"} />
              <Info label="Bay" value={booking.bay_name || "Waiting bay"} />
              <Info label="Mechanic" value={booking.mechanic_name || "Not assigned"} />
              <Info label="Tester" value={booking.tester_name || "Not assigned"} />
            </div>
            {qaFailed && (
              <div className="border border-red-500/30 bg-red-500/10 p-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-400">QA rejected</p>
                <p className="mt-1 text-sm text-red-100">{booking.qa_reason || booking.qa_reject_reason || "Reason not captured"}</p>
              </div>
            )}
            <div className="border border-zinc-800 bg-zinc-900/50 p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-500 mb-4">Service timeline</p>
              <Timeline status={booking.status} rework={qaFailed} />
            </div>
          </div>
          <aside className="space-y-4">
            <div className="border border-zinc-800 bg-zinc-900/50 p-4">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Current status</p>
                <StatusPill status={booking.status} />
              </div>
              <p className="mt-4 font-display text-3xl font-black">₹{booking.bill_amount || 0}</p>
              <p className="font-mono text-xs text-zinc-500">{booking.paid ? "Payment completed" : "Payment pending or not billed"}</p>
            </div>
            <ActionButtons b={booking} onUpdate={onUpdate} compact />
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Info = ({ label, value }) => (
  <div className="border border-zinc-800 bg-zinc-900/50 p-3">
    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</p>
    <p className="mt-1 text-sm font-semibold text-zinc-100">{value}</p>
  </div>
);

const timeSince = (date) => {
  const diff = Date.now() - date.getTime();
  if (!Number.isFinite(diff) || diff < 0) return "";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};
